/* Pet profiles — account hub CRUD, plus a small read-only API other sections use.

   Endpoint and auth are read from data attributes rendered by Liquid. The backend accepts
   either a Shopify App Proxy signature (relative path, no extra params) or a customer_id +
   HMAC pair computed server-side by Liquid. The HMAC is used here because the store's app
   was created in the admin and may not support proxies. Either way the browser never holds
   a secret: it only carries a hash that Liquid produced for THIS customer, and Liquid only
   ever emits the signed-in customer's own id.

   Other sections use window.VPPets.load() to read the cached list; the PDP uses it to
   offer "use Bailey's weight" on the dosage calculator. */
(function (w, d) {
  'use strict';

  // Endpoint + auth come from the rendered markup so Liquid can compute the HMAC. If an
  // App Proxy is ever configured, point data-endpoint at the relative /apps/... path and
  // leave the auth attributes off — the backend accepts either.
  var cfg = d.querySelector('[data-vp-pets-config]') || d.querySelector('[data-vp-pets]');
  var ENDPOINT = (cfg && cfg.dataset.endpoint) || '/apps/vetpro/pets';
  var AUTH = (cfg && cfg.dataset.customerId && cfg.dataset.hash)
    ? ('customer_id=' + encodeURIComponent(cfg.dataset.customerId) + '&hash=' + encodeURIComponent(cfg.dataset.hash))
    : '';
  function url(path, extra) {
    var qs = [AUTH, extra].filter(Boolean).join('&');
    return ENDPOINT + (path || '') + (qs ? (ENDPOINT.indexOf('?') > -1 ? '&' : '?') + qs : '');
  }
  var CACHE_KEY = 'vp_pets_cache';
  var cache = null;      // in-memory for this page
  var inflight = null;

  /* sessionStorage is per-tab, and invalidate() only reaches the tab that made the change.
     Add a pet on the account page and a PDP already open in another tab keeps serving its
     own copy of the old list — indefinitely, since a stale non-empty list is a cache hit.
     A short expiry bounds that to minutes without giving up the benefit; these reads exist
     to spare a request per page view, not to hold data for a whole session. */
  var CACHE_TTL_MS = 5 * 60 * 1000;

  function readSession() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      // Entries written before this were a bare array with no timestamp. Treat them as
      // expired rather than trusting them forever — that's the exact staleness being fixed.
      if (!parsed || !Array.isArray(parsed.pets) || typeof parsed.t !== 'number') return null;
      if (Date.now() - parsed.t > CACHE_TTL_MS) return null;
      return parsed.pets;
    } catch (e) { return null; }
  }
  function writeSession(pets) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ pets: pets, t: Date.now() })); } catch (e) {}
  }
  function clearSession() {
    try { sessionStorage.removeItem(CACHE_KEY); } catch (e) {}
  }

  /* Resolves to an array (possibly empty). Never rejects — a signed-out visitor gets 401
     and callers should simply render nothing rather than an error. */
  function load(force) {
    // An empty list is treated as a cache MISS, not as "this customer has no pets". A failed
    // request used to be coerced to [] and written to sessionStorage, so any error cached
    // itself as a legitimate answer for the rest of the session — and because [] is truthy,
    // every later call short-circuited to it without retrying. That outlived the bug that
    // caused it: the config was missing outside the account page, every PDP cached an empty
    // list, and fixing the config changed nothing until the tab was closed. Re-fetching for
    // a genuinely petless customer costs one request; replaying a cached failure costs the
    // feature.
    if (!force && cache && cache.length) return Promise.resolve(cache);
    if (!force) {
      var cached = readSession();
      if (cached && cached.length) { cache = cached; return Promise.resolve(cache); }
    }
    if (inflight) return inflight;
    inflight = fetch(url(''), { headers: { Accept: 'application/json' } })
      // Reject rather than coerce, so a 401/404 can never reach writeSession() below.
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('pets: HTTP ' + r.status)); })
      .then(function (data) {
        cache = (data && data.pets) || [];
        writeSession(cache);
        return cache;
      })
      .catch(function () { cache = null; return []; })
      .then(function (pets) { inflight = null; return pets; });
    return inflight;
  }

  function send(method, path, body) {
    return fetch(url(path), {
      method: method,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok) throw new Error(j.error || 'Something went wrong. Please try again.');
        return j;
      });
    });
  }

  w.VPPets = {
    load: load,
    invalidate: function () { cache = null; clearSession(); },
    create: function (fields) { return send('POST', '', fields); },
    update: function (id, fields) { return send('PATCH', '/' + encodeURIComponent(id), fields); },
    remove: function (id) { return send('DELETE', '/' + encodeURIComponent(id)); },
    photoUrl: function (id) { return send('GET', '/' + encodeURIComponent(id) + '/photo'); },
    removePhoto: function (id) { return send('DELETE', '/' + encodeURIComponent(id) + '/photo'); },

    /* Upload flow: ask for a signed URL, PUT the file straight to storage, then record the
       path on the pet. The service key never reaches the browser. */
    uploadPhoto: function (id, file) {
      return send('POST', '/' + encodeURIComponent(id) + '/photo', { contentType: file.type, size: file.size })
        .then(function (r) {
          return fetch(r.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file })
            .then(function (up) {
              if (!up.ok) throw new Error('Upload failed. Please try again.');
              return send('PATCH', '/' + encodeURIComponent(id), { photo_path: r.path });
            });
        });
    }
  };

  /* ---------------------------------------------------------------- account hub UI */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Birthdays are captured as month + year. Plenty of dogs are rescues whose owners don't
     know the exact day, and nothing downstream uses it — age, the puppy/adult/senior band and
     the Klaviyo sync all work from the month. Asking for a day added friction and invited
     invented data.

     Stored as the 1st so the column stays a real date and every existing calculation keeps
     working untouched. Records saved before this change keep their real day; the field just
     shows the month. */
  function monthValue(birthdate) {
    return birthdate ? String(birthdate).slice(0, 7) : '';
  }
  function monthToDate(value) {
    if (!value) return '';
    return value.length === 7 ? value + '-01' : value;
  }

  function ageFrom(birthdate) {
    if (!birthdate) return '';
    var b = new Date(birthdate + 'T00:00:00');
    if (isNaN(b)) return '';
    var months = (Date.now() - b.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
    if (months < 1) return 'under a month';
    if (months < 24) return Math.round(months) + ' mo';
    return Math.floor(months / 12) + ' yr';
  }

  /* The dialog is shared. It's rendered once per page from theme.liquid, but two roots can
     exist on the account page — the pets card and the global headless host — and each runs
     init(). Attaching the dialog's own listeners per root would double-fire submit and
     create the pet twice. Whichever root initialises first owns the dialog; the rest wire
     only their own list. */
  var dialogWired = false;

  /* Lets any page react to a change made in the dialog. The PDP's weight picker is built
     from the pet list, so without this it would keep showing the old weight after an edit
     until the page was reloaded. */
  function notifyChanged() {
    try { d.dispatchEvent(new CustomEvent('vp:pets-updated')); } catch (e) {}
  }

  function init(root) {
    if (!root || root.dataset.init) return;
    root.dataset.init = '1';

    var listEl = root.querySelector('[data-pets-list]');
    var emptyEl = root.querySelector('[data-pets-empty]');
    var errEl = root.querySelector('[data-pets-error]');
    // The dialog is fixed-position and lives outside the card, so query the document.
    var dlg = d.querySelector('[data-pets-dialog]');
    var formEl = dlg && dlg.querySelector('[data-pets-form]');
    var addBtn = root.querySelector('[data-pets-add]');
    var titleEl = dlg && dlg.querySelector('[data-pets-formtitle]');
    var dlgErr = dlg && dlg.querySelector('[data-pets-dlgerror]');
    var photoPrev = dlg && dlg.querySelector('[data-pets-photopreview]');
    var photoPick = dlg && dlg.querySelector('[data-pets-photopick]');
    var photoClear = dlg && dlg.querySelector('[data-pets-photoclear]');
    var seedWeight = root.dataset.seedWeight || '';
    if (!dlg || !formEl) return;

    var editingId = null;
    var pendingPhoto = null;   // File chosen before a new pet exists
    var releaseTrap = null;

    // Errors show inside the dialog when it's open, on the card otherwise.
    function fail(msg) {
      var target = (!dlg.hidden && dlgErr) ? dlgErr : errEl;
      if (!target) return;
      target.textContent = msg;
      target.hidden = !msg;
      if (target === dlgErr && errEl) errEl.hidden = true;
    }

    function setPhotoPreview(src) {
      if (!photoPrev) return;
      photoPrev.style.backgroundImage = src ? 'url("' + src + '")' : '';
      photoPrev.classList.toggle('has-img', !!src);
      if (photoClear) photoClear.hidden = !src;
      if (photoPick) photoPick.textContent = src ? 'Change photo' : 'Add a photo';
    }

    function showForm(pet) {
      editingId = pet ? pet.id : null;
      pendingPhoto = null;
      if (titleEl) titleEl.textContent = pet ? 'Edit ' + pet.name : 'Add a pet';
      formEl.name.value = pet ? pet.name : '';
      formEl.weight_lb.value = pet && pet.weight_lb != null ? pet.weight_lb : (pet ? '' : seedWeight);
      formEl.breed.value = pet && pet.breed ? pet.breed : '';
      formEl.birthdate.value = monthValue(pet && pet.birthdate);
      setPhotoPreview('');
      if (pet && pet.photo_path) {
        w.VPPets.photoUrl(pet.id).then(function (r) { if (r && r.url) setPhotoPreview(r.url); }).catch(function () {});
      }
      dlg.hidden = false;
      d.body.style.overflow = 'hidden';
      fail('');
      formEl.name.focus();
      if (w.VPFocusTrap && !releaseTrap) releaseTrap = w.VPFocusTrap.trap(dlg);
    }
    function hideForm() {
      editingId = null;
      pendingPhoto = null;
      dlg.hidden = true;
      d.body.style.overflow = '';
      fail('');
      if (releaseTrap) { releaseTrap(); releaseTrap = null; }
    }

    function render(pets) {
      // Headless host: it exists only to own the dialog, and has nothing to draw.
      if (!listEl) return;
      listEl.innerHTML = pets.map(function (p) {
        var bits = [];
        if (p.weight_lb != null) bits.push(p.weight_lb + ' lb');
        var age = ageFrom(p.birthdate);
        if (age) bits.push(age);
        if (p.breed) bits.push(esc(p.breed));
        var avatar = p.photo_path
          ? '<span class="vp-pets__avatar vp-pets__avatar--img" data-pet-photo="' + esc(p.id) + '" aria-hidden="true"></span>'
          : '<span class="vp-pets__avatar" aria-hidden="true">' + esc((p.name || '?').charAt(0).toUpperCase()) + '</span>';
        return '<li class="vp-pets__item">' +
            '<div class="vp-pets__who">' +
              avatar +
              '<div>' +
                '<p class="vp-pets__name">' + esc(p.name) + '</p>' +
                (bits.length ? '<p class="vp-pets__meta">' + bits.join(' · ') + '</p>' : '') +
              '</div>' +
            '</div>' +
            '<div class="vp-pets__acts">' +
              '<button type="button" class="vp-pets__link" data-pet-edit="' + esc(p.id) + '">Edit</button>' +
              '<button type="button" class="vp-pets__link vp-pets__link--danger" data-pet-del="' + esc(p.id) + '">Remove</button>' +
            '</div>' +
          '</li>';
      }).join('');
      if (emptyEl) emptyEl.hidden = pets.length > 0;
      if (root.hasAttribute('hidden') && listEl) root.hidden = false;
      // Photos live in a private bucket, so each needs a short-lived signed URL.
      listEl.querySelectorAll('[data-pet-photo]').forEach(function (el) {
        w.VPPets.photoUrl(el.getAttribute('data-pet-photo'))
          .then(function (r) { if (r && r.url) el.style.backgroundImage = 'url("' + r.url + '")'; })
          .catch(function () {});
      });
    }

    function refresh(force) {
      return load(force).then(render);
    }

    root.addEventListener('click', function (e) {
      var el;
      if ((el = e.target.closest('[data-pet-edit]'))) {
        var id = el.getAttribute('data-pet-edit');
        load().then(function (pets) {
          var pet = pets.filter(function (p) { return p.id === id; })[0];
          if (pet) showForm(pet);
        });
        return;
      }
      if ((el = e.target.closest('[data-pet-del]'))) {
        var did = el.getAttribute('data-pet-del');
        // Deliberately not a confirm() — a browser modal blocks the page and can strand
        // automation. Two-step on the button itself instead.
        if (el.dataset.confirm !== '1') {
          el.dataset.confirm = '1';
          el.textContent = 'Tap to confirm';
          setTimeout(function () {
            if (el.dataset.confirm === '1') { el.dataset.confirm = ''; el.textContent = 'Remove'; }
          }, 4000);
          return;
        }
        el.disabled = true;
        w.VPPets.remove(did)
          .then(function () { w.VPPets.invalidate(); notifyChanged(); return refresh(true); })
          .catch(function (err) { fail(err.message); el.disabled = false; el.textContent = 'Remove'; el.dataset.confirm = ''; });
        return;
      }
      if (e.target.closest('[data-pets-add]')) { showForm(null); return; }
    });

    if (!dialogWired) {
    dialogWired = true;

    dlg.addEventListener('click', function (e) {
      if (e.target.closest('[data-pets-cancel]')) { hideForm(); return; }
      if (e.target.closest('[data-pets-photoclear]')) {
        pendingPhoto = null;
        setPhotoPreview('');
        if (editingId) {
          w.VPPets.removePhoto(editingId)
            .then(function () { w.VPPets.invalidate(); return refresh(true); })
            .catch(function (err) { fail(err.message); });
        }
        return;
      }
      if (e.target.closest('[data-pets-photopick]')) {
        // capture="environment" opens the rear camera on phones; desktop gets a file dialog.
        var picker = d.createElement('input');
        picker.type = 'file';
        picker.accept = 'image/jpeg,image/png,image/webp';
        picker.setAttribute('capture', 'environment');
        picker.addEventListener('change', function () {
          var file = picker.files && picker.files[0];
          if (!file) return;
          if (file.size > 5 * 1024 * 1024) { fail('That image is over 5 MB — please pick a smaller one.'); return; }
          setPhotoPreview(URL.createObjectURL(file));
          if (editingId) {
            w.VPPets.uploadPhoto(editingId, file)
              .then(function () { w.VPPets.invalidate(); return refresh(true); })
              .catch(function (err) { fail(err.message); });
          } else {
            // A new pet has no id yet — hold the file and upload once it's created.
            pendingPhoto = file;
          }
        });
        picker.click();
        return;
      }
    });

    d.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !dlg.hidden) hideForm();
    });

    formEl.addEventListener('submit', function (e) {
      e.preventDefault();
      var payload = {
        name: formEl.name.value,
        weight_lb: formEl.weight_lb.value === '' ? null : formEl.weight_lb.value,
        breed: formEl.breed.value,
        birthdate: monthToDate(formEl.birthdate.value)
      };
      var btn = formEl.querySelector('[type="submit"]');
      btn.disabled = true;
      var req = editingId ? w.VPPets.update(editingId, payload) : w.VPPets.create(payload);
      req.then(function (res) {
        if (!editingId && pendingPhoto && res && res.pet) {
          return w.VPPets.uploadPhoto(res.pet.id, pendingPhoto).catch(function () {});
        }
      }).then(function () {
        w.VPPets.invalidate();
        notifyChanged();
        hideForm();
        return refresh(true);
      }).catch(function (err) {
        fail(err.message);
      }).then(function () { btn.disabled = false; });
    });

    // Opening the dialog from anywhere — the PDP weight step uses these.
    w.VPPets.edit = function (id) {
      return load().then(function (pets) {
        var pet = pets.filter(function (p) { return String(p.id) === String(id); })[0];
        if (pet) showForm(pet);
        return pet || null;
      });
    };
    w.VPPets.add = function () { showForm(null); };

    }   // end dialogWired

    refresh(false);
  }

  d.addEventListener('DOMContentLoaded', function () {
    d.querySelectorAll('[data-vp-pets]').forEach(init);
  });
  // Theme editor re-renders sections without a page load.
  d.addEventListener('shopify:section:load', function () {
    d.querySelectorAll('[data-vp-pets]').forEach(init);
  });
})(window, document);
